import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Button from './Button';

// Shared shell for the two filing-state transitions — mark filed (captures
// filing date + optional reference) and unfile (requires a logged reason).
// Portaled to document.body for the same reason as ConfirmModal: `fixed`
// must resolve against the real viewport regardless of where this renders.
const FilingActionModal = ({ isOpen, mode, loading, onCancel, onConfirm }) => {
  const [filedAt, setFiledAt] = useState('');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFiledAt(new Date().toISOString().split('T')[0]);
      setReference('');
      setReason('');
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const isFile = mode === 'file';

  const handleSubmit = () => {
    if (isFile) {
      onConfirm({ filedAt, reference });
    } else if (reason.trim()) {
      onConfirm(reason.trim());
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl border border-ink-line shadow-card-hover p-6 w-full max-w-sm">
        <h2 className="text-lg font-medium text-ink">{isFile ? 'Mark as filed' : 'Reopen this return'}</h2>
        <p className="text-sm text-ink-body mt-1">
          {isFile
            ? 'Records the filing date and reference for this period.'
            : 'Explain why this return is being reopened — this is logged.'}
        </p>

        {isFile ? (
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-xs font-medium text-ink-body mb-1">Filing date</label>
              <input
                type="date"
                value={filedAt}
                onChange={(e) => setFiledAt(e.target.value)}
                className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-ink-body mb-1">Reference number (optional)</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="BIR confirmation / filing reference"
                className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <label className="block text-xs font-medium text-ink-body mb-1">Reason *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" size="md" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            loading={loading}
            disabled={!isFile && !reason.trim()}
          >
            {isFile ? 'Mark as filed' : 'Reopen return'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default FilingActionModal;
